/**
 * @module app.ext.iccc
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, } from 'app/shared/lib/essette-site';
import { AssessmentService } from 'app/core/services/assessment-service';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { IBaseLocalState } from 'app/shared/models/local-state-extensions';
import { guid } from 'app/shared/lib/guid';
import * as Core from 'app/core/models/essette-api';
import * as Iccc from 'app/ext/iccc/interfaces';
import * as CM from 'app/cm/models/essette-api';

export type CaseAssessmentAttachmentWithLocalState = ExtendLocalState<CM.CaseAssessmentAttachment, IBaseLocalState>;

export type ChoiceSummary = {
    label: string,
    response: boolean
};

export type QuestionSummary = {
    label: string,
    hasChoices: boolean,
    hasAnswers: boolean,
    choices: ChoiceSummary[] | null,
    response: string | null
};

export type SectionSummary = {
    label: string,
    questions: QuestionSummary[] | null
};

@autoinject()
export class AssessmentSummary extends ComponentBase {

    ////****************** Fields and Properties **************************************////

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IAssessmentsConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'loadData' })
    public attachmentId: string | null = null;

    protected assessment: Iccc.Assessment | Partial<Iccc.Assessment> | null = null;
    protected attachment: CaseAssessmentAttachmentWithLocalState | null = null;
    protected session: Core.AssessmentSession | null = null;

    protected sections: SectionSummary[] = [];

    protected assessmentTextSummary?: string;
    protected iccCopyrightDisclaimer?: string;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly service: AssessmentService,
        ...rest: any[]) {

        super(...rest);
    }

    protected async bind(): Promise<void> {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.attachmentId) { throw new Error('Bindable property [attachment-id] was not set'); }

        this.service.configure(this.config);
        this.loadData(this.attachmentId);
    }

    protected detached(): void {
        this.sections = [];
    }

    protected reset(): void {
        this.assessment = null;
        this.attachment = null;
        this.sections = [];
        this.session = null;
    }

    ////****************** Functions ***********************************************////

    private async loadData(attachmentId: string): Promise<void> {
        this.reset();
        await this.loadAttachment(attachmentId);
        if(!this.attachment) { throw new Error('No assessment attachment was loaded'); }
        this.iccCopyrightDisclaimer = undefined;
        if(this.attachment.assessment?.externalSystem && this.attachment.assessment.externalSystem.code?.toLowerCase() == 'iccc') {
            const content = this.attachment.externalContent?.content;
            const copyright = content ? this.getIccCopyright(content) : undefined;
            if(copyright) this.iccCopyrightDisclaimer = copyright;
        }

        let assessment: CM.CaseAssessmentWithVersionModel | null | undefined = this.attachment.assessment;
        if(!assessment) { throw new Error('No assessment was loaded'); }

        if(this.attachment.lastSession && this.attachment.lastSession.id) await this.loadSession(this.attachment.lastSession.id);

        this.loadIcccAssessmentStructure();
    }

    private loadIcccAssessmentStructure() {
        if(!this.attachment?.externalContent?.mediaType?.code) return;

        const content = this.attachment.externalContent.content;
        if(!content) throw new Error('Assessment attachment content was not loaded');

        if(this.attachment.externalContent.mediaType.code === 'data.json') {
            // parse json (for older ICCC assessments)
            this.assessment = JSON.parse(content);
            if(!this.assessment || !this.assessment.iccc_sections || this.assessment.iccc_sections.length === 0) throw new Error('Error parsing assessment json');

            this.assessment.iccc_sections.forEach(s => {
                s.iccc_sub_sections.forEach(ss => {
                    const questions: QuestionSummary[] = [];

                    ss.iccc_questions.forEach(q => {
                        const choices: ChoiceSummary[] = [];

                        q.iccc_choices?.forEach(c => {
                            choices.push({
                                label: c.label.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/<HTML>/g, '').replace(/<\/HTML>/g, '').replace(/<BR \/>/g, '').replace(/<B>/g, '').replace(/<\/B>/g, '').replace(/<I>/g, '').replace(/<\/I>/g, ''),
                                response: c.response
                            });
                        });

                        const question: QuestionSummary = {
                            label: q.label.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/<HTML>/g, '').replace(/<\/HTML>/g, '').replace(/<BR \/>/g, '').replace(/<B>/g, '').replace(/<\/B>/g, '').replace(/<I>/g, '').replace(/<\/I>/g, ''),
                            response: q.response,
                            hasChoices: choices.length > 0,
                            hasAnswers: choices.some(c => c.response),
                            choices: choices.length > 0 ? [...choices] : null
                        };

                        questions.push(question);
                    });

                    const section: SectionSummary = {
                        label: ss.label,
                        questions: questions
                    };

                    this.sections.push(section);
                });

            });
        } else if(this.attachment.externalContent.mediaType.code === 'data.xml') {
            // parse xml
            const parser = new DOMParser();
            const review: Document = parser.parseFromString(content, "text/xml");
            if(!review) return;

            // parse assessment text summary
            const assessmentTextSummary = review.getElementsByTagName('CarePlan')[0]?.getElementsByTagName('Assessments')[0]?.getElementsByTagName('Assessment')[0]?.getElementsByTagName('AssessmentTextSummary')[0]?.textContent;
            if(assessmentTextSummary) this.assessmentTextSummary = assessmentTextSummary.trim();

            const label = review.getElementsByTagName('Assessment')[0]?.getAttribute("SubsetDesc");
            const version = review.getElementsByTagName('Assessment')[0]?.getAttribute("VersionDesc");
            this.assessment = {
                label: label || '',
                version: version || ''
            };
        }
    }

    private getIccCopyright(content: string): string | undefined {
        if(!content) return undefined;

        const parser = new DOMParser();
        const review: Document = parser.parseFromString(content, 'text/xml');
        const copyright = review.documentElement?.getAttribute('copyright')?.trim();
        return copyright;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAttachment(attachmentId: string): Promise<void> {
        const includes: string[] = [
            'assessment.externalSystem',
            'currentAnswers',
            'externalContent',
            'externalContent.mediaType',
            'metadata.created.byUser'
        ];

        const fields: string[] = [
            'assessment.externalSystem.code',
            'assessment.id',
            'assessment.version.id',
            'externalContent.content',
            'externalContent.isContentEncrypted',
            'externalContent.mediaType.code',
            'isCompleted',
            'lastSession.id',
            'metadata.created.byUser.description',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadAttachment(attachmentId, includes, undefined, fields);
            if(!response.isSuccessful || response.results.length === 0) return;

            const attachment: CM.CaseAssessmentAttachment = response.results[0];
            this.attachment = attachment;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSession(sessionId: string): Promise<void> {
        const includes: string[] = ['metadata.modified.byUser'];
        const fields: string[] = [
            'metadata.modified.byUser.description',
            'metadata.modified.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadSession(sessionId, includes, fields);
            if(!response.isSuccessful || response.results.length === 0) return;
            this.session = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
